

(function () {
  'use strict';

  const INTERVAL_MS    = 1800;
  const RESET_DELAY_MS = 120;

  // State per card:
  // { active, timer, pos, originalPos, originalSlideId, originalSwatchInput }
  const state = new WeakMap();

  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Slideshow / slide helpers ─────────────────────────────────────────────

  function getSW(card) {
    return card.querySelector('slideshow-component') || null;
  }

  /**
   * Full raw slide array — includes hidden variant slides.
   * sw.refs.slides is unfiltered; sw.slides filters out hidden ones.
   */
  function allSlides(card) {
    const sw = getSW(card);
    if (!sw) return [];
    if (sw.refs?.slides?.length) return Array.from(sw.refs.slides);
    return Array.from(card.querySelectorAll('slideshow-slide'));
  }

  // ── Swatch helpers ────────────────────────────────────────────────────────

  function swatchForSlideId(card, slideId) {
    return card.querySelector(
      `input[type="radio"][data-option-media-id="${slideId}"]`
    ) || null;
  }

  function checkedSwatch(card) {
    return card.querySelector(
      'input[type="radio"]:checked[data-option-media-id]'
    ) || null;
  }

  /** Check a swatch input and fire change so product-card.js syncs. */
  function selectSwatch(input) {
    if (!input || input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── Core advance ──────────────────────────────────────────────────────────

  /**
   * Move to s.pos in the raw slide list.
   * Handles unhiding variant slides before calling sw.select().
   * Does NOT read sw.current — uses s.pos tracked entirely by us.
   */
  function advanceToPos(card) {
    const sw = getSW(card);
    const s  = state.get(card);
    if (!sw || !s) return;

    const slides = allSlides(card);
    if (slides.length < 2) return;

    // Clamp in case slides were added/removed
    s.pos = s.pos % slides.length;

    const slide   = slides[s.pos];
    if (!slide) return;

    const slideId   = slide.getAttribute('slide-id');
    const isVariant = slide.hasAttribute('variant-image');

    if (isVariant && slideId) {
      // Unhide this variant slide; hide all other variant slides.
      // We do this BEFORE sw.select() so the slideshow can scroll to it.
      slides.forEach((sl) => {
        if (sl.hasAttribute('variant-image')) {
          if (sl === slide) {
            sl.removeAttribute('hidden');
            sl.setAttribute('aria-hidden', 'false');
          } else {
            sl.hidden = true;
            sl.setAttribute('aria-hidden', 'true');
          }
        }
      });
    }

    // Use the slideshow's own select so scroll / aria / dots all work
    if (slideId) {
      sw.select({ id: slideId }, undefined, { animate: true });
    } else {
      sw.select(s.pos, undefined, { animate: true });
    }

    // Sync swatch AFTER select so product-card.js can read the updated DOM
    if (isVariant && slideId) {
      // Use a microtask so product-card.js's slideshow:select handler runs first
      Promise.resolve().then(() => {
        if (!state.get(card)?.active) return; // cycling stopped in the meantime
        const swatch = swatchForSlideId(card, slideId);
        selectSwatch(swatch);
      });
    }
  }

  // ── Cycle control ─────────────────────────────────────────────────────────

  function startCycle(card) {
    if (reducedMotion()) return;

    const slides = allSlides(card);
    if (slides.length < 2) return;

    const s = state.get(card);
    if (!s || s.active) return;

    const sw = getSW(card);

    // Snapshot starting position and swatch for restore on leave
    // Find which raw-slide index is currently showing
    const currentSlide = sw?.refs?.slides?.[sw.current];
    s.originalPos         = currentSlide ? slides.indexOf(currentSlide) : 0;
    s.originalSlideId     = currentSlide?.getAttribute('slide-id') || null;
    s.originalSwatchInput = checkedSwatch(card);

    // Start one position ahead
    s.pos    = (s.originalPos + 1) % slides.length;
    s.active = true;
    card.setAttribute('data-cs-active', '');

    // Advance immediately on first tick
    advanceToPos(card);

    s.timer = setInterval(() => {
      if (!s.active) return;
      s.pos = (s.pos + 1) % allSlides(card).length;
      advanceToPos(card);
    }, INTERVAL_MS);
  }

  function stopCycle(card, restore = false) {
    const s = state.get(card);
    if (!s) return;

    s.active = false;
    clearInterval(s.timer);
    s.timer = null;
    card.removeAttribute('data-cs-active');

    if (!restore) return;

    setTimeout(() => {
      const sw = getSW(card);
      if (!sw) return;

      // If user clicked a different swatch while cycling, honour their choice
      const current = checkedSwatch(card);
      if (current && s.originalSwatchInput && current !== s.originalSwatchInput) return;

      // Restore original swatch
      if (s.originalSwatchInput) {
        selectSwatch(s.originalSwatchInput);
      }

      // Restore variant-image hidden states
      const slides = allSlides(card);
      slides.forEach((sl) => {
        if (sl.hasAttribute('variant-image')) {
          const isOriginal = sl.getAttribute('slide-id') === s.originalSlideId;
          sl.hidden = !isOriginal;
          sl.setAttribute('aria-hidden', String(!isOriginal));
        }
      });

      // Return to original slide
      if (s.originalSlideId) {
        sw.select({ id: s.originalSlideId }, undefined, { animate: false });
      } else {
        sw.select(s.originalPos || 0, undefined, { animate: false });
      }
    }, RESET_DELAY_MS);
  }

  // ── Dot indicators ────────────────────────────────────────────────────────

  function addDots(card) {
    const total = allSlides(card).length;
    if (total < 2) return;
    if (card.querySelector('.cs-dots')) return;

    const gallery = card.querySelector('.card-gallery');
    const sw      = getSW(card);
    if (!gallery || !sw) return;

    const wrap = document.createElement('div');
    wrap.className = 'cs-dots';
    wrap.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < total; i++) {
      const dot = document.createElement('span');
      dot.className = 'cs-dot' + (i === 0 ? ' cs-dot--active' : '');
      wrap.appendChild(dot);
    }
    gallery.appendChild(wrap);

    // Sync dots to our s.pos, not sw.current
    sw.addEventListener('slideshow:select', () => {
      const s    = state.get(card);
      const idx  = s?.active ? s.pos : (s?.originalPos ?? 0);
      const dots = wrap.querySelectorAll('.cs-dot');
      dots.forEach((d, i) => d.classList.toggle('cs-dot--active', i === idx));
    });
  }

  // ── Attach ────────────────────────────────────────────────────────────────

  function attachCard(card) {
    if (state.has(card)) return;
    state.set(card, {
      active: false,
      timer: null,
      pos: 0,
      originalPos: 0,
      originalSlideId: null,
      originalSwatchInput: null,
    });

    if (allSlides(card).length < 2) return;

    addDots(card);

    const gallery = card.querySelector('.card-gallery');
    if (!gallery) return;

    gallery.addEventListener('pointerenter', () => startCycle(card), { passive: true });
    gallery.addEventListener('pointerleave', () => stopCycle(card, true),  { passive: true });

    // Swatch click: clear snapshot so stopCycle won't override user's pick
    const picker = card.querySelector('swatches-variant-picker-component');
    if (picker) {
      picker.addEventListener('pointerdown', () => {
        const s = state.get(card);
        if (s) {
          s.originalSwatchInput = null;
          s.originalSlideId     = null;
        }
        stopCycle(card, false);
      }, { passive: true });
    }
  }

  // ── MutationObserver — infinite scroll ────────────────────────────────────

  function setupMutationObserver() {
    const grid = document.querySelector('results-list, .product-grid-container');
    if (!grid) return;

    new MutationObserver((mutations) => {
      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (!(node instanceof Element)) continue;
          node.querySelectorAll?.('product-card').forEach(attachCard);
          if (node.matches?.('product-card')) attachCard(node);
        }
      }
    }).observe(grid, { childList: true, subtree: true });
  }

  // ── CSS ───────────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('cs-styles')) return;

    const s = document.createElement('style');
    s.id = 'cs-styles';
    s.textContent = `
      /* card-slideshow v4 */
      .card-gallery { position: relative; }

      .cs-dots {
        position: absolute;
        bottom: 8px;
        left: 0; right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        z-index: 4;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
      product-card[data-cs-active] .cs-dots { opacity: 1; }

      .cs-dot {
        width: 5px; height: 5px;
        border-radius: 50%;
        background: rgba(255,255,255,0.5);
        transition: background 0.18s ease, transform 0.18s ease;
        flex-shrink: 0;
      }
      .cs-dot--active {
        background: #fff;
        transform: scale(1.4);
      }

      @media (prefers-reduced-motion: reduce) {
        .cs-dots { display: none !important; }
      }
    `;
    document.head.appendChild(s);
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function boot() {
    injectStyles();
    document.querySelectorAll('product-card').forEach(attachCard);
    setupMutationObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

}());